import { Component, ForceMode, PhysXComponent, Property } from '@wonderlandengine/api';
import { FPSMovement } from './movement';
import { AudioSource } from '@wonderlandengine/components';

/**
 * GravityCanon
 * Reliably launches a player object using Trigger events.
 */
export class GravityCanon extends Component {
    static TypeName = 'gravity-canon';

    static Properties = {
        player: Property.object(),
        yForce: Property.float(50.0),
        zForce: Property.float(100.0),
        flyTime: Property.float(1500),
        cooldown: Property.float(500),
    };

    /* Pre-allocated buffers for stable math and zero GC allocations */
    _forward = new Float32Array(3);
    _up = new Float32Array(3);
    _launchVector = new Float32Array(3);
    _zeroVec = new Float32Array([0, 0, 0]);
    _isLocked = false;
    _lockTimer = 0.0;
    _flyTimer = 0.0;
    _wasAirborne = false;

    init() {
        // Pre-bind collision callback to avoid allocating a new function in start() or during execution
        this._onCollisionBound = this.onCollision.bind(this);
    }

    start() {
        if (!this.player) {
            console.error(`${this.object.name} -> GravityCanon: Player object is missing!`);
            return;
        }

        // Cache components to minimize JS-to-WASM bridge calls during gameplay
        this.audio = this.object.getComponent(AudioSource);
        this.movement = this.player.getComponent(FPSMovement);
        this.physx = this.object.getComponent(PhysXComponent);
        this.playerPhysx = this.player.getComponent(PhysXComponent);

        this.object.getForwardWorld(this._forward);
        this.object.getUpWorld(this._up);

        // Construct the launch vector once ahead of time
        this._launchVector[0] = (this._forward[0] * this.zForce) + (this._up[0] * this.yForce);
        this._launchVector[1] = (this._forward[1] * this.zForce) + (this._up[1] * this.yForce);
        this._launchVector[2] = (this._forward[2] * this.zForce) + (this._up[2] * this.yForce);

        if (this.physx) {
            this.physx.onCollision(this._onCollisionBound);
        }
    }

    update(dt) {
        // Use the update loop instead of setTimeout to prevent closure allocations and GC pauses
        if (this._isLocked) {
            this._lockTimer -= dt;
            if (this._lockTimer <= 0.0) {
                this._isLocked = false;
            }
        }

        if (this._wasAirborne) {
            this._flyTimer -= dt;
            if (this._flyTimer <= 0.0) {
                this._wasAirborne = false;
                if (this.movement) {
                    this.movement.isAirborne = false;
                }
            }
        }
    }

    onCollision(type, other) {
        /* type 2 = TriggerTouch (The start of the overlap)
           We only want to fire once when the player enters.
        */
        // Use objectId integer comparison instead of string allocation and comparison
        if (type !== 2 || this._isLocked || other.object.objectId !== this.player.objectId) return;

        if (this.playerPhysx && this.movement) {
            this._isLocked = true;
            this._lockTimer = this.cooldown / 1000.0; // convert ms to seconds
            
            this._wasAirborne = true;
            this._flyTimer = this.flyTime / 1000.0; // convert ms to seconds
            
            this.movement.isAirborne = true;
            
            // Use pre-allocated zero vector to avoid array creation [0,0,0]
            this.playerPhysx.linearVelocity = this._zeroVec;
            this.playerPhysx.angularVelocity = this._zeroVec;

            // Apply impulse using the pre-calculated launch vector
            this.playerPhysx.addForce(this._launchVector, ForceMode.Impulse, true);

            if (this.audio) this.audio.play();
        }
    }
}
